import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '@/services/api';

/**
 * "Invite a contact" — a registered customer's personal referral code + one-tap WhatsApp share,
 * plus their active reward vouchers. Opened in the context of a branch so the share link carries
 * that bar, and both the customer and their contact earn the bar's configured discount.
 */
const InviteContact: React.FC = () => {
  const { branchId } = useParams<{ branchId: string }>();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [branchName, setBranchName] = useState('');
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!apiService.isAuthenticated()) { navigate('/login'); return; }
    (async () => {
      try {
        const res = await apiService.getReferralCode();
        setCode(((res as any)?.data?.code) || '');
      } catch (e) { setErr(e instanceof Error ? e.message : 'Could not load your code'); }
      try {
        const v = await apiService.getReferralVouchers(branchId);
        setVouchers(((v as any)?.data) || []);
      } catch { /* ignore */ }
      if (branchId) {
        try { const b = await apiService.getBranchDetails(branchId); setBranchName(((b as any)?.data?.name) || ''); } catch { /* ignore */ }
      }
    })();
  }, [branchId, navigate]);

  const link = code && branchId ? `${window.location.origin}/refer/${branchId}/${code}` : '';
  const shareText = `Join me at ${branchName || 'this venue'} — use my link and we both get a discount 🎉\n${link}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(link || code); }
    catch {
      const ta = document.createElement('textarea'); ta.value = link || code; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select(); try { document.execCommand('copy'); } catch { /* ignore */ } document.body.removeChild(ta);
    }
    setCopied(true); window.setTimeout(() => setCopied(false), 1600);
  };
  const whatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');

  return (
    <div style={s.wrap}>
      <button style={s.back} onClick={() => navigate(-1)}>← Back</button>
      <h1 style={s.h1}>🎁 Invite a contact</h1>
      <p style={s.sub}>Share your link. When your contact joins {branchName ? `${branchName}` : 'the venue'} and visits, you both get a discount.</p>

      {err && <div style={s.err}>{err}</div>}

      <div style={s.card}>
        <div style={s.label}>Your personal code</div>
        <div style={s.code}>{code || '…'}</div>
        {link && <div style={s.link}>{link}</div>}
        <div style={s.row}>
          <button style={s.primary} onClick={whatsapp} disabled={!link}>🟢 Share on WhatsApp</button>
          <button style={s.ghost} onClick={copy} disabled={!code}>{copied ? '✓ Copied' : '📋 Copy link'}</button>
        </div>
        {!branchId && <div style={s.hint}>Open this from a venue to get a shareable link for that bar.</div>}
      </div>

      <h2 style={s.h2}>My rewards</h2>
      {vouchers.length === 0 ? (
        <div style={s.empty}>No rewards yet. Invite a contact to earn one.</div>
      ) : (
        <div style={s.vlist}>
          {vouchers.map((v) => (
            <div key={v.id} style={s.voucher}>
              <div style={s.vval}>
                {v.rewardType === 'percent' ? `${Number(v.rewardValue)}% off` : `${v.currency ? v.currency + ' ' : ''}${Number(v.rewardValue)} off`}
              </div>
              <div style={s.vmeta}>
                {v.role === 'referrer' ? 'For referring a contact' : v.role === 'loyalty' ? 'Loyalty reward' : 'Welcome reward'}
                {v.expiresAt ? ` · expires ${new Date(v.expiresAt).toLocaleDateString()}` : ''}
              </div>
              {v.code && <div style={s.vcode}>{v.code}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  wrap: { maxWidth: 560, margin: '0 auto', padding: 16 },
  back: { background: 'none', border: 'none', color: '#6b7280', fontWeight: 700, cursor: 'pointer', padding: '6px 0', fontSize: 14 },
  h1: { fontSize: 24, fontWeight: 800, margin: '6px 0 4px', color: '#111827' },
  h2: { fontSize: 16, fontWeight: 800, margin: '20px 0 8px', color: '#111827' },
  sub: { color: '#6b7280', margin: '0 0 14px', fontSize: 14 },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  label: { fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#9ca3af' },
  code: { fontSize: 28, fontWeight: 800, letterSpacing: '.06em', color: '#b45309', margin: '4px 0 6px' },
  link: { fontSize: 12.5, color: '#6b7280', wordBreak: 'break-all', marginBottom: 12 },
  row: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  primary: { flex: 1, minWidth: 160, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 14px', fontWeight: 800, cursor: 'pointer' },
  ghost: { background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 10, padding: '11px 14px', fontWeight: 700, cursor: 'pointer' },
  hint: { color: '#9ca3af', fontSize: 12.5, marginTop: 10 },
  empty: { color: '#9ca3af', fontSize: 14, padding: '10px 0' },
  vlist: { display: 'flex', flexDirection: 'column', gap: 10 },
  voucher: { background: '#fff', border: '1px dashed #f59e0b', borderRadius: 12, padding: 14 },
  vval: { fontSize: 18, fontWeight: 800, color: '#b45309' },
  vmeta: { fontSize: 12.5, color: '#6b7280', marginTop: 2 },
  vcode: { fontSize: 12, fontWeight: 700, color: '#374151', marginTop: 6, letterSpacing: '.05em' },
  err: { background: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 10, padding: '8px 12px', marginBottom: 12, fontSize: 14 },
};

export default InviteContact;
